import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, FileSearch, Search } from 'lucide-react';
import { fastapiService } from '../../services/fastapiService';
import type { AdminAction } from '../../types';

function formatTs(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function escapeCsvCell(val: string): string {
  if (/[",\n\r]/.test(val)) return `"${val.replace(/"/g, '""')}"`;
  return val;
}

function buildAuditCsv(data: AdminAction[]): string {
  const headers = [
    'timestamp',
    'action',
    'user_id',
    'actor_email',
    'resource_type',
    'resource_id',
    'workspace_id',
    'metadata_json',
    'ip',
    'user_agent',
  ];
  const lines = [headers.join(',')];
  for (const r of data) {
    const d = (r.details || {}) as Record<string, unknown>;
    const row = [
      escapeCsvCell(formatTs(r.timestamp)),
      escapeCsvCell(String(r.action || '')),
      escapeCsvCell(String(r.user_id || '')),
      escapeCsvCell(String(d.actor_email || '')),
      escapeCsvCell(String(d.resource_type || '')),
      escapeCsvCell(String(d.resource_id || '')),
      escapeCsvCell(String(d.workspace_id || '')),
      escapeCsvCell(JSON.stringify(d.metadata ?? {})),
      escapeCsvCell(String(d.ip_address || '')),
      escapeCsvCell(String(d.user_agent || '')),
    ];
    lines.push(row.join(','));
  }
  return lines.join('\r\n');
}

function downloadTextFile(filename: string, text: string, mime: string) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const EnterpriseAuditPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState<AdminAction[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const { data, pagination } = await fastapiService.getRecentActions({
          page,
          limit,
          search: search || undefined,
        });
        if (cancelled) return;
        setRows(data || []);
        setTotal(pagination?.total ?? 0);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load audit log');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [page, limit, search]);

  const applySearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const exportCurrentPage = () => {
    const csv = buildAuditCsv(rows);
    downloadTextFile(`admin-audit-page-${page}.csv`, csv, 'text/csv;charset=utf-8');
  };

  const exportLatest500 = async () => {
    try {
      setExporting(true);
      const { data } = await fastapiService.getRecentActions({
        page: 1,
        limit: 500,
        search: search || undefined,
      });
      const csv = buildAuditCsv(data || []);
      downloadTextFile('admin-audit-latest-500.csv', csv, 'text/csv;charset=utf-8');
    } catch (e) {
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-amber-900">
          <FileSearch className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-enterprise-900">Audit log</h2>
          <p className="mt-1 text-sm text-enterprise-600">
            Platform admin mutations: users, subscriptions, API keys, workspace keys &amp; credits, email provider, phone pool
            &amp; verifications, workflow redrive, log clears, and sign-ins. Events are stored after DB migration{' '}
            <code className="rounded bg-enterprise-100 px-1">286_platform_admin_audit_log</code>; until then the list
            stays empty but the API returns safely.
          </p>
        </div>
      </div>

      <form onSubmit={applySearch} className="flex flex-wrap items-end gap-3 rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
        <div className="min-w-[200px] flex-1">
          <label htmlFor="audit-search" className="block text-xs font-medium text-enterprise-500">
            Filter actions
          </label>
          <div className="relative mt-1">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-enterprise-400" />
            <input
              id="audit-search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full rounded-lg border border-enterprise-200 py-2 pl-9 pr-3 text-sm text-enterprise-900 outline-none focus:border-emerald-500"
              placeholder="e.g. user.delete, api_keys"
            />
          </div>
        </div>
        <button
          type="submit"
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={exportCurrentPage}
          disabled={rows.length === 0}
          className="inline-flex items-center gap-2 rounded-lg border border-enterprise-200 bg-white px-4 py-2 text-sm font-medium text-enterprise-800 hover:bg-enterprise-50 disabled:opacity-40"
        >
          <Download className="h-4 w-4" />
          CSV (this page)
        </button>
        <button
          type="button"
          onClick={exportLatest500}
          disabled={exporting}
          className="inline-flex items-center gap-2 rounded-lg border border-enterprise-200 bg-white px-4 py-2 text-sm font-medium text-enterprise-800 hover:bg-enterprise-50 disabled:opacity-40"
        >
          <Download className="h-4 w-4" />
          {exporting ? 'Export…' : 'CSV (latest 500)'}
        </button>
      </form>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <p className="py-12 text-center text-enterprise-500">Loading…</p>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-enterprise-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-enterprise-100 text-left text-sm">
              <thead className="bg-enterprise-50 text-xs font-semibold uppercase tracking-wide text-enterprise-500">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Actor</th>
                  <th className="px-4 py-3">Target</th>
                  <th className="px-4 py-3">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-enterprise-100">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-enterprise-500">
                      No events yet. Actions are recorded when covered admin endpoints run successfully.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const d = r.details as Record<string, unknown> | undefined;
                    const actor = (d?.actor_email as string) || r.user_id?.slice(0, 8) || '—';
                    const target =
                      d?.resource_type && d?.resource_id
                        ? `${String(d.resource_type)}:${String(d.resource_id)}`
                        : (d?.workspace_id as string) || '—';
                    return (
                      <tr key={r.id} className="align-top hover:bg-enterprise-50/80">
                        <td className="whitespace-nowrap px-4 py-3 text-enterprise-600">{formatTs(r.timestamp)}</td>
                        <td className="px-4 py-3 font-mono text-xs text-enterprise-900">{r.action}</td>
                        <td className="max-w-[180px] truncate px-4 py-3 text-enterprise-700" title={r.user_id}>
                          {actor}
                        </td>
                        <td className="max-w-[200px] truncate px-4 py-3 font-mono text-xs text-enterprise-600" title={target}>
                          {target}
                        </td>
                        <td className="max-w-md px-4 py-3">
                          <pre className="max-h-24 overflow-auto whitespace-pre-wrap break-all text-[11px] text-enterprise-600">
                            {d?.metadata != null ? JSON.stringify(d.metadata, null, 0) : '—'}
                          </pre>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-enterprise-600">
            <span>
              Page {page} of {totalPages} ({total} events)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center gap-1 rounded-lg border border-enterprise-200 px-3 py-1.5 font-medium text-enterprise-800 disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center gap-1 rounded-lg border border-enterprise-200 px-3 py-1.5 font-medium text-enterprise-800 disabled:opacity-40"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
